import {
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  ListItemIcon,
  makeStyles,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import {
  Close,
  FilterTiltShift,
  LooksOne,
  LooksTwo,
  TrackChanges,
} from '@material-ui/icons'
import { Badge, DisabledByDefault, VerticalShades } from '@mui/icons-material'
import React, { useEffect, useState } from 'react'
import {
  SaveButton,
  SimpleForm,
  Toolbar,
  ToolbarProps,
  useTranslate,
} from 'react-admin'
import { Form } from 'react-final-form'
import { StanbyMode } from '../../../core/enum/StanbyMode'
import { ResourceName } from '../../../core/ResourceName'
import { ConfirmDialog } from '../../common/ConfirmDialog'
import Button from '../../common/customized-mui-components/Button'
import DraggableComponent from '../../common/DraggableComponent'
import FilteredReferenceInput from '../../common/FilteredReferenceInput'
import { EventDto } from '../../../core/dto/event/event.dto'
import { DoorDto } from '../../../core/dto/device/doors/door.dto'
import { DoorWorkingModes } from '../../../core/enum/DoorWorkingModes'
import { DoorTypes } from '../../../core/enum/DoorTypes'
import { DateTime } from '../../../core/common/date-time'

const useStyles = makeStyles({
  bulkStateActions: {
    height: '16px',
    background: 'rgba(0,0,0,0.2)',
    minWidth: '300px',
    userSelect: 'none',
  },
  bulkProgressTitle: {
    paddingLeft: '10px',
    width: '100%',
  },
  bulkProgressCloseButton: {
    margin: 0,
  },
  actionsButton: {
    margin: '2%',
    width: '96%',
  },
  iconContainer: {
    width: 34,
    height: 34,
  },
  icon: {
    position: 'relative',
    left: 5,
    top: 5,
    fontSize: '24px !important',
  },
  crossIcon: {
    position: 'relative',
    left: -24,
    top: 0,
    fontSize: 34,
    opacity: 0.8,
  },
  formToolbar: {
    backgroundColor: 'inherit',
    padding: 0,
    margin: 0,
  },
  dialogContent: {
    padding: '8px',
  },
})

export const SetStandbyModeDialog = ({
  open,
  close,
  standbyMode,
  setStandbyMode,
  handleStandbyMode,
  asRecurringJob,
}: {
  open: boolean
  close: () => void
  standbyMode?: StanbyMode
  setStandbyMode: (value: StanbyMode) => void
  handleStandbyMode: (
    recurringJobName?: string,
    cronExpression?: string,
    scheduledAt?: DateTime,
  ) => void
  asRecurringJob?: boolean
}) => {
  const classes = useStyles()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const translate = useTranslate()

  const handleClose = () => {
    setConfirmOpen(false)
    close()
  }

  const handleSetStandbyMode = (
    recurringJobName?: string,
    cronExpression?: string,
    scheduledAt?: DateTime,
  ) => {
    if (asRecurringJob)
      handleStandbyMode(recurringJobName, cronExpression, scheduledAt)
    else handleStandbyMode()
    handleClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
      onClick={(e) => e.stopPropagation()}
    >
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => setConfirmOpen(false)}
        confirm={handleSetStandbyMode}
        source="setStandbyMode"
        action="set"
        resource={ResourceName.DOORS}
        asRecurringJob={asRecurringJob}
      />
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate('resources.doors.dialogs.setStandbyMode.title')}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent>
        <Select
          variant="outlined"
          value={standbyMode}
          onChange={(e) => setStandbyMode(e.target.value as StanbyMode)}
          fullWidth
          displayEmpty
        >
          <MenuItem value={StanbyMode.ALWAYS_ON}>
            <ListItemIcon className={classes.iconContainer}>
              <TrackChanges className={classes.icon} />
            </ListItemIcon>
            {translate(`resources.enums.standbyMode.${StanbyMode.ALWAYS_ON}`)}
          </MenuItem>
          <MenuItem value={StanbyMode.ALWAYS_OFF}>
            <ListItemIcon className={classes.iconContainer}>
              <TrackChanges className={classes.icon} />
              <Close className={classes.crossIcon} />
            </ListItemIcon>
            {translate(`resources.enums.standbyMode.${StanbyMode.ALWAYS_OFF}`)}
          </MenuItem>
          <MenuItem value={StanbyMode.SENSOR}>
            <ListItemIcon className={classes.iconContainer}>
              <FilterTiltShift className={classes.icon} />
            </ListItemIcon>
            {translate(`resources.enums.standbyMode.${StanbyMode.SENSOR}`)}
          </MenuItem>
        </Select>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          label={translate('ra.action.save')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
          disabled={
            !Object.values(StanbyMode).includes(standbyMode as StanbyMode)
          }
        />
        <Button
          variant="contained"
          onClick={handleClose}
          label={translate('const.cancel')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
      </DialogActions>
    </Dialog>
  )
}

export const DoorLockDialog = ({
  open,
  close,
  disabledId,
  setDisabledId,
  handleLock,
  asRecurringJob,
}: {
  open: boolean
  close: () => void
  disabledId: number
  setDisabledId: (value: number) => void
  handleLock: (recurringJobName?: string, cronExpression?: string) => void
  asRecurringJob?: boolean
}) => {
  const classes = useStyles()
  const [error, setError] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const translate = useTranslate()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const handleClose = () => {
    setDisabledId(0)
    setError(false)
    setErrorText('')
    setConfirmOpen(false)
    close()
  }

  const handleConfirm = (
    recurringJobName?: string,
    cronExpression?: string,
  ) => {
    if (asRecurringJob) handleLock(recurringJobName, cronExpression)
    else handleLock()
    handleClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
      onClick={(e) => e.stopPropagation()}
    >
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => setConfirmOpen(false)}
        confirm={handleConfirm}
        source="lock"
        action="set"
        resource={ResourceName.DOORS}
        asRecurringJob={asRecurringJob}
      />
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate('resources.doors.dialogs.lock.title')}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={close}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent>
        <Form onSubmit={() => undefined}>
          {(props) => (
            <form onSubmit={props.handleSubmit}>
              <FilteredReferenceInput
                source="disabled"
                defaultValue={null}
                reference={ResourceName.VERIFIER_DEVICE_DISABLED}
                sort={{ field: 'name', order: 'ASC' }}
                perPage={smallScreen ? 5 : 15}
                allowEmpty
                filterSource="name"
                selectWithPaginationInputProps={{
                  optionText: 'name',
                  showFilter: true,
                  error,
                  helperText: errorText,
                }}
                fullWidth
                onChange={(e) => setDisabledId(e.target.value)}
                value={disabledId}
                resource={ResourceName.DOORS}
              />
            </form>
          )}
        </Form>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          label={translate('ra.action.save')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
        <Button
          variant="contained"
          onClick={handleClose}
          label={translate('const.cancel')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
      </DialogActions>
    </Dialog>
  )
}

export const SetWorkingModeDialog = ({
  open,
  close,
  workingMode,
  setWorkingMode,
  handleSetWorkingMode,
  asRecurringJob,
  door,
}: {
  open: boolean
  close: () => void
  workingMode?: DoorWorkingModes
  setWorkingMode: (value: DoorWorkingModes) => void
  handleSetWorkingMode: (
    recurringJobName?: string,
    cronExpression?: string,
  ) => void
  asRecurringJob?: boolean
  door?: DoorDto
}) => {
  const classes = useStyles()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const translate = useTranslate()

  const handleClose = () => {
    setConfirmOpen(false)
    close()
  }

  const handleWorkingMode = (
    recurringJobName?: string,
    cronExpression?: string,
  ) => {
    if (asRecurringJob) handleSetWorkingMode(recurringJobName, cronExpression)
    else handleSetWorkingMode()
    handleClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
      onClick={(e) => e.stopPropagation()}
    >
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => setConfirmOpen(false)}
        confirm={handleWorkingMode}
        source="setWorkingMode"
        action="set"
        resource={ResourceName.DOORS}
        asRecurringJob={asRecurringJob}
      />
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate('resources.doors.dialogs.setWorkingMode.title')}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent>
        <Select
          variant="outlined"
          value={workingMode}
          onChange={(e) => setWorkingMode(e.target.value as DoorWorkingModes)}
          fullWidth
          displayEmpty
        >
          <MenuItem value={DoorWorkingModes.DoorReleased}>
            <ListItemIcon className={classes.iconContainer}>
              <VerticalShades className={classes.icon} />
            </ListItemIcon>
            {translate(
              `resources.enums.DoorWorkingModes.${DoorWorkingModes.DoorReleased}`,
            )}
          </MenuItem>
          <MenuItem value={DoorWorkingModes.CardVerification}>
            <ListItemIcon className={classes.iconContainer}>
              <Badge className={classes.icon} />
            </ListItemIcon>
            {translate(
              `resources.enums.DoorWorkingModes.${DoorWorkingModes.CardVerification}`,
            )}
          </MenuItem>
          {door?.type === DoorTypes.DOUBLE_READER && (
            <MenuItem value={DoorWorkingModes.FirstReaderDisabled}>
              <ListItemIcon className={classes.iconContainer}>
                <LooksOne className={classes.icon} />
              </ListItemIcon>
              {translate(
                `resources.enums.DoorWorkingModes.${DoorWorkingModes.FirstReaderDisabled}`,
              )}
            </MenuItem>
          )}
          {door?.type === DoorTypes.DOUBLE_READER && (
            <MenuItem value={DoorWorkingModes.SecondReaderDisabled}>
              <ListItemIcon className={classes.iconContainer}>
                <LooksTwo className={classes.icon} />
              </ListItemIcon>
              {translate(
                `resources.enums.DoorWorkingModes.${DoorWorkingModes.SecondReaderDisabled}`,
              )}
            </MenuItem>
          )}
          <MenuItem value={DoorWorkingModes.AllReadersDisabled}>
            <ListItemIcon className={classes.iconContainer}>
              <DisabledByDefault className={classes.icon} />
            </ListItemIcon>
            {translate(
              `resources.enums.DoorWorkingModes.${DoorWorkingModes.AllReadersDisabled}`,
            )}
          </MenuItem>
        </Select>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          label={translate('ra.action.save')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
          disabled={!workingMode}
        />
        <Button
          variant="contained"
          onClick={handleClose}
          label={translate('const.cancel')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
      </DialogActions>
    </Dialog>
  )
}

const validateAttachEvents = (values: DoorDto) => {
  const errors: { [n: string]: string } = {}
  if (!values.id) {
    errors.id = 'ra.validation.required'
  }
  return errors
}

export const DoorEventsRecurringDialog = ({
  open,
  close,
  setSelectedEventId,
  handleAttachment,
  method,
}: {
  open: boolean
  close: () => void
  setSelectedEventId: (value: number | undefined) => void
  handleAttachment: (recurringJobName?: string, cronExpression?: string) => void
  method: 'attach' | 'detach'
}) => {
  const classes = useStyles()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const translate = useTranslate()
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const handleClose = () => {
    setConfirmOpen(false)
    close()
  }

  const Actions = (props: ToolbarProps) => (
    <Toolbar {...props} className={classes.formToolbar}>
      <SaveButton
        variant="contained"
        size="small"
        className={classes.actionsButton}
        onSave={(values) => {
          setSelectedEventId((values as EventDto)?.id)
          setConfirmOpen(true)
        }}
        onSubmit={() => setConfirmOpen(true)}
      />
      <Button
        variant="contained"
        onClick={handleClose}
        label={translate('const.cancel')}
        useSmallVersionBreakpoint={false}
        className={classes.actionsButton}
      />
    </Toolbar>
  )

  useEffect(() => {
    if (!open) {
      setSelectedEventId(undefined)
      setConfirmOpen(false)
    }
  }, [open, setSelectedEventId, setConfirmOpen])

  const handleConfirm = (
    recurringJobName?: string,
    cronExpression?: string,
  ) => {
    handleAttachment(recurringJobName, cronExpression)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
      onClick={(e) => e.stopPropagation()}
    >
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => {
          setSelectedEventId(undefined)
          setConfirmOpen(false)
        }}
        confirm={handleConfirm}
        source={`${method}Events`}
        action="set"
        resource={ResourceName.DOORS}
        asRecurringJob
      />
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate(`resources.doors.dialogs.${method}Events.title`)}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={handleClose}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogContent className={classes.dialogContent}>
        <SimpleForm toolbar={<Actions />} validate={validateAttachEvents}>
          <FilteredReferenceInput
            label={`resources.doors.dialogs.${method}Events.fields.eventId`}
            source="id"
            reference={ResourceName.EVENTS}
            sort={{ field: 'id', order: 'DESC' }}
            perPage={smallScreen ? 5 : 15}
            filterSource="search"
            required
            selectWithPaginationInputProps={{
              optionText: 'name',
              showFilter: true,
            }}
          />
        </SimpleForm>
      </DialogContent>
    </Dialog>
  )
}

export const DoorReleaseDialog = ({
  open,
  close,
  handleSetReleased,
}: {
  open: boolean
  close: () => void
  handleSetReleased: (released: boolean) => void
}) => {
  const classes = useStyles()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const translate = useTranslate()
  const [released, setReleased] = useState<boolean | undefined>(undefined)

  const handleClose = () => {
    setConfirmOpen(false)
    close()
  }

  const handleConfirm = () => {
    handleClose()
    handleSetReleased(released as boolean)
  }

  const handleOpenConfirm = (value: boolean) => {
    setReleased(value)
    setConfirmOpen(true)
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      disableEscapeKeyDown
      PaperComponent={DraggableComponent}
      aria-labelledby="draggable-dialog-title"
      onClick={(e) => e.stopPropagation()}
    >
      <ConfirmDialog
        open={confirmOpen}
        cancel={() => setConfirmOpen(false)}
        confirm={handleConfirm}
        source="setState"
        action={released ? 'set' : 'remove'}
        resource={ResourceName.DOORS}
      />
      <DialogActions
        className={classes.bulkStateActions}
        style={{ cursor: 'move' }}
      >
        <Typography
          className={classes.bulkProgressTitle}
          id="draggable-dialog-title"
        >
          {translate('resources.doors.dialogs.setState.title')}
        </Typography>
        <IconButton
          size="small"
          className={classes.bulkProgressCloseButton}
          onClick={close}
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogActions>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => handleOpenConfirm(true)}
          label={translate('resources.doors.dialogs.setState.actions.release')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
        <Button
          variant="contained"
          onClick={() => handleOpenConfirm(false)}
          label={translate('resources.doors.dialogs.setState.actions.withhold')}
          useSmallVersionBreakpoint={false}
          className={classes.actionsButton}
        />
      </DialogActions>
    </Dialog>
  )
}
